import React, { useState, useEffect, useMemo } from 'react';
import {
  Alert,
  Card,
  Row,
  Col,
  Progress,
  Tag,
  Timeline,
  Button,
  Space,
  Typography,
  Divider,
  Spin,
  Statistic,
  Layout,
  Menu
} from 'antd';
import {
  ExclamationCircleOutlined,
  HeartOutlined,
  ExperimentOutlined,
  FireOutlined,
  SafetyOutlined,
  MedicineBoxOutlined,
  CalendarOutlined,
  PhoneOutlined,
  FileTextOutlined,
  SettingOutlined,
  UserOutlined,
  DownloadOutlined,
  DashboardOutlined,
  HistoryOutlined,
  BookOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import usePortalHealthScoring from '../hooks/usePortalHealthScoring';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { MAX_Z_FOR_ZERO_HEALTH } from '../utils/scoringEngineV2';

const { Title, Text, Paragraph } = Typography;
const { Sider, Content } = Layout;

const ModernPatientHealthOverview = () => {
  const {
    tableData,
    overallDeviation,
    systemDeviations,
    lifestyleDeviations,
    loading,
    error
  } = usePortalHealthScoring();

  // Restructure data to match expected format
  const healthScoringData = useMemo(() => ({
    tableData,
    overallDeviation,
    systemDeviations,
    lifestyleDeviations
  }), [tableData, overallDeviation, systemDeviations, lifestyleDeviations]);

  const [isMobile, setIsMobile] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState('overview');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate metrics from actual data
  const overallWellnessPercentage = useMemo(() => {
    if (!overallDeviation?.average || loading || error) return 0;
    return Math.max(0, Math.min(100, Math.round(100 - (overallDeviation.average / MAX_Z_FOR_ZERO_HEALTH) * 100)));
  }, [overallDeviation, loading, error]);

  const totalParameterCount = useMemo(() => {
    if (!tableData) return 0;
    return tableData.reduce((total, category) => total + (category.parameters?.length || 0), 0);
  }, [tableData]);

  const parametersWithZScores = useMemo(() => {
    if (!tableData) return 0;
    let count = 0;
    tableData.forEach(category => {
      category.parameters?.forEach(param => {
        if (param.zScore !== null && param.zScore !== undefined && !isNaN(param.zScore)) {
          count++;
        }
      });
    });
    return count;
  }, [tableData]);

  const avgZScore = useMemo(() => {
    return overallDeviation?.average ? overallDeviation.average.toFixed(2) : '0.00';
  }, [overallDeviation]);

  const highRiskSystems = useMemo(() => {
    if (!systemDeviations) return 0;
    let count = 0;
    Object.values(systemDeviations).forEach(system => {
      const avgScore = ((system.structure?.healthScore || 0) + (system.function?.healthScore || 0) + (100 - (system.risk?.healthScore || 0))) / 3;
      if (avgScore < 60) count++;
    });
    return count;
  }, [systemDeviations]);

  // Calculate health systems data from actual scoring results
  const healthSystemsData = useMemo(() => {
    if (!healthScoringData || !healthScoringData.systemDeviations || loading || error) {
      return [
        { name: 'Neurological', value: 0, color: '#667eea', icon: '🧠', score: 0, risk: 'UNKNOWN' },
        { name: 'Cardiovascular', value: 0, color: '#f093fb', icon: '❤️', score: 0, risk: 'UNKNOWN' },
        { name: 'Respiratory', value: 0, color: '#4facfe', icon: '🫁', score: 0, risk: 'UNKNOWN' },
        { name: 'Metabolic', value: 0, color: '#43e97b', icon: '⚡', score: 0, risk: 'UNKNOWN' },
        { name: 'Immune', value: 0, color: '#fa709a', icon: '🛡️', score: 0, risk: 'UNKNOWN' },
        { name: 'Musculoskeletal', value: 0, color: '#a8edea', icon: '🦴', score: 0, risk: 'UNKNOWN' },
        { name: 'Filtration', value: 0, color: '#ff9a9e', icon: '🫘', score: 0, risk: 'UNKNOWN' }
      ];
    }

    const { systemDeviations } = healthScoringData;
    const systemConfig = [
      { key: 'neurological', name: 'Neurological', color: '#667eea', icon: '🧠' },
      { key: 'cardiovascular', name: 'Cardiovascular', color: '#f093fb', icon: '❤️' },
      { key: 'respiratory', name: 'Respiratory', color: '#4facfe', icon: '🫁' },
      { key: 'metabolic', name: 'Metabolic', color: '#43e97b', icon: '⚡' },
      { key: 'immune', name: 'Immune', color: '#fa709a', icon: '🛡️' },
      { key: 'musculoskeletal', name: 'Musculoskeletal', color: '#a8edea', icon: '🦴' },
      { key: 'filtration', name: 'Filtration', color: '#ff9a9e', icon: '🫘' }
    ];

    return systemConfig.map(system => {
      const systemData = systemDeviations[system.key];
      if (!systemData) {
        return { ...system, value: 0, score: 0, risk: 'UNKNOWN' };
      }

      // Calculate average health score across Structure, Function, Risk dimensions
      const structureScore = systemData.structure?.healthScore || 0;
      const functionScore = systemData.function?.healthScore || 0;
      const riskScore = 100 - (systemData.risk?.healthScore || 0); // Invert risk score
      const averageScore = (structureScore + functionScore + riskScore) / 3;

      // Determine risk level based on average score
      let risk = 'LOW';
      if (averageScore < 60) risk = 'HIGH';
      else if (averageScore < 80) risk = 'MEDIUM';

      return {
        ...system,
        value: Math.round(averageScore),
        score: systemData.structure?.averageWeightedAbsZ || 0,
        risk: risk
      };
    });
  }, [healthScoringData, loading, error]);

  // Calculate lifestyle data from actual scoring results
  const lifestyleData = useMemo(() => {
    if (!healthScoringData || !healthScoringData.lifestyleDeviations || loading || error) {
      return [
        { name: 'Nutrition', value: 0, color: '#667eea', icon: '🥗' },
        { name: 'Movement & Exercise', value: 0, color: '#f093fb', icon: '🏃‍♂️' },
        { name: 'Recovery & Sleep', value: 0, color: '#4facfe', icon: '😴' },
        { name: 'Mental Health', value: 0, color: '#43e97b', icon: '🧘‍♂️' }
      ];
    }

    const { lifestyleDeviations } = healthScoringData;
    const lifestyleConfig = [
      { key: 'nutrition', name: 'Nutrition', color: '#667eea', icon: '🥗' },
      { key: 'movement', name: 'Movement & Exercise', color: '#f093fb', icon: '🏃‍♂️' },
      { key: 'recovery', name: 'Recovery & Sleep', color: '#4facfe', icon: '😴' },
      { key: 'mental_health', name: 'Mental Health', color: '#43e97b', icon: '🧘‍♂️' }
    ];

    return lifestyleConfig.map(lifestyle => {
      const lifestyleDataEntry = lifestyleDeviations[lifestyle.key];
      const healthScore = lifestyleDataEntry?.healthScore;

      return {
        ...lifestyle,
        value: typeof healthScore === 'number' ? Math.round(healthScore) : 0
      };
    });
  }, [healthScoringData, loading, error]);

  const pieChartData = healthSystemsData.map(item => ({
    name: item.name,
    value: item.value,
    fill: item.color
  }));

  const timelineEvents = [
    {
      type: 'MEDICATION',
      title: 'Started Amlodipine 10mg',
      description: 'Increased dosage to better control hypertension. Take once daily in the morning.',
      date: '12/08/2025 - 09:30',
      color: '#4CAF50'
    },
    {
      type: 'LABORATORY',
      title: 'Blood Chemistry Panel - Results',
      description: 'Glucose: 95 mg/dL (Normal), Cholesterol: 220 mg/dL (High), HbA1c: 6.2%',
      date: '10/08/2025 - 14:15',
      color: '#FF9800'
    },
    {
      type: 'APPOINTMENT',
      title: 'Follow-up Consultation - Dr. Nguyen',
      description: 'Reviewed blood pressure management. Discussed lifestyle modifications and medication adjustments.',
      date: '08/08/2025 - 10:00',
      color: '#2196F3'
    },
    {
      type: 'LIFESTYLE',
      title: 'Nutrition Counseling Session',
      description: 'DASH diet education completed. Patient provided with meal planning resources and sodium reduction guidelines.',
      date: '05/08/2025 - 16:30',
      color: '#9C27B0'
    }
  ];

  const quickActions = [
    { title: 'Schedule Appointment', icon: <CalendarOutlined />, color: '#667eea', description: 'Book with your care team' },
    { title: 'View Lab Results', icon: <ExperimentOutlined />, color: '#f093fb', description: 'Latest test results' },
    { title: 'Medication List', icon: <MedicineBoxOutlined />, color: '#4facfe', description: 'Current prescriptions' },
    { title: 'Contact Care Team', icon: <PhoneOutlined />, color: '#43e97b', description: 'Message your doctors' },
    { title: 'Medical Records', icon: <DownloadOutlined />, color: '#fa709a', description: 'Download documents' },
    { title: 'Settings', icon: <SettingOutlined />, color: '#a8edea', description: 'Preferences & privacy' }
  ];

  const renderCircularProgress = (percentage, size = 120) => (
    <div style={{ 
      width: size, 
      height: size, 
      position: 'relative', 
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Progress
        type="circle"
        percent={percentage}
        width={size}
        strokeColor="#4CAF50"
        trailColor="#e0e0e0"
        strokeWidth={8}
        format={() => (
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: size > 100 ? '1.75rem' : '1.1rem', 
              fontWeight: '800', 
              color: '#4CAF50',
              letterSpacing: '-0.02em'
            }}>
              {percentage}%
            </div>
          </div>
        )}
      />
    </div>
  );

  const sidebarSections = [
    {
      title: 'Health Dashboard',
      items: [
        { key: 'overview', icon: '🏠', label: 'Health Overview', active: true },
        { key: 'problems', icon: '⚠️', label: 'Problem List', badge: '3' },
        { key: 'goals', icon: '🎯', label: 'Smart Goals', badge: '5' },
        { key: 'actionplan', icon: '📋', label: 'Action Plan', badge: '2' }
      ]
    },
    {
      title: 'Health Data',
      items: [
        { key: 'vitals', icon: '📊', label: 'Vitals' },
        { key: 'medications', icon: '💊', label: 'Medications' },
        { key: 'labs', icon: '🔬', label: 'Lab Results' },
        { key: 'timeline', icon: '⏰', label: 'Timeline' },
        { key: 'allergies', icon: '🚨', label: 'Allergies' }
      ]
    },
    {
      title: 'Personal Info',
      items: [
        { key: 'demographics', icon: '👤', label: 'Demographics' },
        { key: 'medhistory', icon: '📜', label: 'Medical History' },
        { key: 'careteam', icon: '👨‍⚕️', label: 'Care Team' },
        { key: 'documents', icon: '📄', label: 'Documents' }
      ]
    }
  ];

  // Early returns after all hooks
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error loading health data"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Left Sidebar Menu */}
      <Sider 
        width={280}
        style={{ 
          background: '#ffffff',
          borderRight: '1px solid #e8e8e8',
          boxShadow: '2px 0 8px rgba(0,0,0,0.06)'
        }}
        breakpoint="lg"
        collapsedWidth={isMobile ? 0 : 80}
        trigger={null}
      >
        <div style={{ 
          padding: '1.5rem 1rem',
          borderBottom: '1px solid #e8e8e8',
          textAlign: 'center'
        }}>
          <Title level={4} style={{ 
            margin: 0, 
            color: '#2c5aa0',
            fontSize: '1.1rem',
            fontWeight: '600'
          }}>
            Dashboard Menu
          </Title>
        </div>
        
        <Menu
          mode="inline"
          selectedKeys={[selectedMenuItem]}
          onClick={({ key }) => setSelectedMenuItem(key)}
          style={{ 
            border: 'none',
            background: 'transparent',
            padding: '1rem 0.5rem'
          }}
          items={sidebarSections.flatMap(section =>
            section.items.map(item => ({
              key: item.key,
              icon: item.icon,
              label: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{item.label}</span>
                  {item.badge && (
                    <Tag color="red" size="small" style={{ marginLeft: '8px' }}>
                      {item.badge}
                    </Tag>
                  )}
                </div>
              ),
              style: {
                borderRadius: '8px',
                margin: '0.25rem 0',
                fontSize: '0.9rem',
                fontWeight: '500'
              }
            }))
          )}
        />
      </Sider>

      {/* Main Content */}
      <Content style={{ background: '#f8f9fa' }}>
        <div style={{ 
          padding: isMobile ? '1rem' : '2rem',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        }}>
          {/* Page Header */}
          <div style={{ marginBottom: '2rem' }}>
            <Title level={2} style={{ 
              color: '#2c5aa0', 
              marginBottom: '0.5rem',
              fontSize: '1.75rem',
              fontWeight: '700',
              letterSpacing: '-0.02em'
            }}>
              Health Overview
            </Title>
            <Text type="secondary" style={{ 
              fontSize: '0.95rem',
              fontWeight: '400',
              color: '#6b7280'
            }}>
              Your comprehensive health dashboard - Last updated: {new Date().toLocaleDateString()}
            </Text>
          </div>

          {/* Alert Banner */}
          <Alert
            message={
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <ExclamationCircleOutlined style={{ fontSize: '1.1rem' }} />
                <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                  <strong>Action Required:</strong> Your blood pressure readings have been consistently high for the past week. Please contact your doctor or schedule an appointment.
                </div>
              </div>
            }
            type="warning"
            showIcon={false}
            style={{ 
              marginBottom: '2rem',
              background: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '1.25rem 1.5rem'
            }}
          />

          {/* Top Overview Grid */}
          <Row gutter={[24, 24]} style={{ marginBottom: '3rem' }}>
            {/* Wellness Score */}
            <Col xs={24} md={8}>
              <Card 
                style={{ 
                  textAlign: 'center', 
                  borderRadius: '20px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: 'none'
                }}
              >
                <Text type="secondary" style={{ 
                  fontSize: '0.85rem', 
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Your Wellness Score
                </Text>
                <div style={{ margin: '1rem 0' }}>
                  {renderCircularProgress(overallWellnessPercentage)}
                </div>
                <Text type="secondary" style={{ 
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  color: '#9ca3af'
                }}>
                  Good Progress
                </Text>
              </Card>
            </Col>

            {/* Health Status Grid */}
            <Col xs={24} md={8}>
              <Row gutter={[12, 12]}>
                <Col span={12}>
                  <Card style={{
                    textAlign: 'center',
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }}>
                    <Statistic
                      title={<span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Total Parameters</span>}
                      value={totalParameterCount}
                      valueStyle={{
                        color: '#2196F3',
                        fontSize: '1.75rem',
                        fontWeight: '700'
                      }}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card style={{
                    textAlign: 'center',
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }}>
                    <Statistic
                      title={<span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>With Z-Scores</span>}
                      value={parametersWithZScores}
                      valueStyle={{
                        color: '#4CAF50',
                        fontSize: '1.75rem',
                        fontWeight: '700'
                      }}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card style={{
                    textAlign: 'center',
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }}>
                    <Statistic
                      title={<span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Avg Z-Score</span>}
                      value={avgZScore}
                      suffix="σ"
                      precision={2}
                      valueStyle={{
                        color: '#FF6B6B',
                        fontSize: '1.75rem',
                        fontWeight: '700'
                      }}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card style={{
                    textAlign: 'center',
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }}>
                    <Statistic
                      title={<span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280' }}>Risk Systems</span>}
                      value={highRiskSystems}
                      valueStyle={{
                        color: '#FF9800',
                        fontSize: '1.75rem',
                        fontWeight: '700'
                      }}
                    />
                  </Card>
                </Col>
              </Row>
            </Col>

            {/* Health Metrics */}
            <Col xs={24} md={8}>
              <Card
                style={{
                  textAlign: 'center',
                  borderRadius: '20px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: 'none'
                }}
              >
                <Text type="secondary" style={{
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Health Metrics
                </Text>
                <div style={{
                  fontSize: '2.75rem',
                  fontWeight: '800',
                  color: '#FF6B6B',
                  margin: '1rem 0 0.5rem',
                  letterSpacing: '-0.02em'
                }}>
                  {avgZScore}
                </div>
                <Text type="secondary" style={{
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  color: '#9ca3af'
                }}>
                  Avg Z-Score (σ)
                </Text>
              </Card>
            </Col>
          </Row>

          {/* Interactive Charts */}
          <Row gutter={[24, 24]} style={{ marginBottom: '3rem' }}>
            <Col xs={24} lg={12}>
              <Card 
                title={<span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937' }}>Health Systems Overview</span>}
                extra={<span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: '500' }}>Interactive Chart</span>}
                style={{ 
                  borderRadius: '16px', 
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: 'none'
                }}
              >
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card 
                title={<span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937' }}>Lifestyle Balance Overview</span>}
                extra={<span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: '500' }}>Interactive Chart</span>}
                style={{ 
                  borderRadius: '16px', 
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: 'none'
                }}
              >
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={lifestyleData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {lifestyleData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          {/* Health Systems at a Glance */}
          <Row gutter={[16, 16]} style={{ marginBottom: '3rem' }}>
            {healthSystemsData.map((system, index) => (
              <Col key={index} xs={24} sm={12} md={8} lg={6}>
                <Card 
                  style={{ 
                    textAlign: 'center',
                    borderRadius: '16px',
                    boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                    transition: 'transform 0.2s ease'
                  }}
                  hoverable
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    margin: '0 auto 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    background: `linear-gradient(135deg, ${system.color}, ${system.color}CC)`
                  }}>
                    {system.icon}
                  </div>
                  
                  <Title level={5} style={{ 
                    marginBottom: '1rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    {system.name}
                  </Title>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    {renderCircularProgress(system.value, 80)}
                  </div>
                  
                  <div style={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: '500' }}>
                    <div style={{ marginBottom: '0.25rem' }}>Benchmark Score: {system.score}</div>
                    <div>
                      Risk Score: 
                      <Tag 
                        color={
                          system.risk === 'LOW' ? 'green' : 
                          system.risk === 'MEDIUM' ? 'orange' : 'red'
                        }
                        style={{ 
                          marginLeft: '4px',
                          fontSize: '0.65rem',
                          fontWeight: '600'
                        }}
                      >
                        {system.risk}
                      </Tag>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Lifestyle Domains */}
          <Card 
            title={
              <Title level={3} style={{ 
                textAlign: 'center', 
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#1f2937'
              }}>
                Lifestyle Domains at a Glance
              </Title>
            }
            style={{ 
              marginBottom: '3rem',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: 'none'
            }}
          >
            <Row gutter={[24, 24]}>
              {lifestyleData.map((lifestyle, index) => (
                <Col key={index} xs={24} sm={12} lg={6}>
                  <div 
                    style={{ 
                      textAlign: 'center',
                      padding: '1.5rem',
                      borderRadius: '12px',
                      background: '#f8f9fa',
                      transition: 'transform 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      margin: '0 auto 1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.8rem',
                      color: 'white',
                      background: `linear-gradient(135deg, ${lifestyle.color}, ${lifestyle.color}CC)`
                    }}>
                      {lifestyle.icon}
                    </div>
                    
                    <Title level={5} style={{ 
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#1f2937'
                    }}>
                      {lifestyle.name}
                    </Title>
                    
                    <Text style={{ 
                      fontSize: '1.1rem', 
                      fontWeight: '700', 
                      color: '#4CAF50'
                    }}>
                      {lifestyle.value}%
                    </Text>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>

          {/* Patient Care Timeline */}
          <Card 
            title={
              <span style={{ 
                fontSize: '1.1rem', 
                fontWeight: '600', 
                color: '#1f2937' 
              }}>
                Patient Care Timeline
              </span>
            }
            extra={
              <Space>
                <Button size="small" style={{ fontSize: '0.8rem', fontWeight: '500' }}>All</Button>
                <Button size="small" type="text" style={{ fontSize: '0.8rem', fontWeight: '500' }}>Medication</Button>
                <Button size="small" type="text" style={{ fontSize: '0.8rem', fontWeight: '500' }}>Laboratory</Button>
                <Button size="small" type="text" style={{ fontSize: '0.8rem', fontWeight: '500' }}>Appointments</Button>
              </Space>
            }
            style={{ 
              marginBottom: '3rem',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: 'none'
            }}
          >
            <Timeline>
              {timelineEvents.map((event, index) => (
                <Timeline.Item 
                  key={index}
                  color={event.color}
                  dot={
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: event.color,
                      border: '3px solid white',
                      boxShadow: '0 0 0 1px #d9d9d9'
                    }} />
                  }
                >
                  <Card size="small" style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <Tag color={event.color} style={{ fontSize: '0.7rem', fontWeight: '600' }}>{event.type}</Tag>
                      <Text type="secondary" style={{ fontSize: '0.75rem', fontWeight: '500' }}>{event.date}</Text>
                    </div>
                    <Title level={5} style={{ 
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#1f2937'
                    }}>{event.title}</Title>
                    <Paragraph style={{ 
                      color: '#6b7280', 
                      fontSize: '0.85rem', 
                      margin: 0,
                      fontWeight: '400'
                    }}>
                      {event.description}
                    </Paragraph>
                  </Card>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>

          {/* Quick Actions Panel */}
          <Card 
            title={
              <Title level={3} style={{ 
                textAlign: 'center', 
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#1f2937'
              }}>
                Quick Actions
              </Title>
            }
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: 'none'
            }}
          >
            <Row gutter={[16, 16]}>
              {quickActions.map((action, index) => (
                <Col key={index} xs={24} sm={12} md={8} lg={4}>
                  <Card 
                    hoverable
                    style={{ 
                      textAlign: 'center',
                      borderRadius: '12px',
                      background: `linear-gradient(135deg, ${action.color}, ${action.color}CC)`,
                      color: 'white',
                      border: 'none',
                      transition: 'transform 0.2s ease'
                    }}
                    bodyStyle={{ padding: '1.5rem' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                      {action.icon}
                    </div>
                    <Title level={5} style={{ 
                      color: 'white', 
                      marginBottom: '0.25rem',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}>
                      {action.title}
                    </Title>
                    <Text style={{ 
                      fontSize: '0.75rem', 
                      opacity: 0.9, 
                      color: 'white',
                      fontWeight: '400'
                    }}>
                      {action.description}
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </div>
      </Content>
    </Layout>
  );
};

export default ModernPatientHealthOverview;